import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export type PlanCode = 'starter' | 'starterPlus';
export type PlanTerm = 'month' | 'year';
export type PlanCurrency = 'usd' | 'eur' | 'gbp'
interface PlanDefinition {
  code: PlanCode;
  label: string;
  description: string;
  features: {
    programsAmount: string;
    trainingSeats: string;
    supportText: string;
    discountText?: string;
    lmsIntegration?: string;
    additionalSeats?: string;
  };
}
export interface Price {
  id: string,
  fullPlanCode: string,
  code: PlanCode,
  term: PlanTerm,
  currency: PlanCurrency,
  unit_amount: number
}

const PLANS: Record<PlanCode, PlanDefinition> = {
  starter: {
    code: 'starter',
    label: 'Starter',
    description: 'Start rolling out your first training program and drive meaningful impact.',
    features: {
      programsAmount: '1 program',
      trainingSeats: '25 training seats',
      supportText: '48h chat support',
      lmsIntegration: 'LMS Integration',
      additionalSeats: 'Additional training seats available *'
    }
  },
  starterPlus: {
    code: 'starterPlus',
    label: 'Starter +',
    description: 'Start rolling out multiple training programs and drive meaningful impact on multiple skills.',
    features: {
      programsAmount: '3 programs',
      trainingSeats: '100 training seats',
      supportText: '24h email/chat support',
      lmsIntegration: 'LMS Integration',
      additionalSeats: 'Additional training seats available *'
    }
  }
};

@Injectable({ providedIn: 'root' })
export class StripeBillingService {
  private base = environment.coursesApiUrl;
  private cache: any[];
  constructor(private http: HttpClient) {}
  getPlans(): Observable<any[]> {
    if (this.cache) {
      return of(this.cache);
    }
    return this.http.get<Price[]>(`${this.base}/api/stripe/prices`).pipe(
      map((prices) => this.initPlansWithPrices(prices)), // <-- transform the value
      tap((plans) => (this.cache = plans)) // <-- cache the transformed value
    );
  }
  initPlansWithPrices(prices: Price[]): any{
    const plans = Object.fromEntries((Object.keys(PLANS) as PlanCode[]).map((code) => [code, { ...PLANS[code], prices: {} as any }]));
    for (const price of prices) {
      const { code, term, currency } = price;
      const plan = plans[code];
      if (!plan.prices[term]) {
        plan.prices[term] = {};
      }
      plan.prices[term][currency] = {
        priceId: price.id,
        fullPlanCode: price.fullPlanCode,
        unit_amount: price.unit_amount
      };
    }
    return plans;
  }
  createCheckoutSession(body: { metadata?: Record<string, string> }) {
    return this.http.post<{ client_secret: string }>(`${this.base}/api/stripe/checkout-session`, body);
  }

  // Optional: server-verified lookup for your return page (does NOT grant access).
  confirmReturn(payload: { session_id?: string; subscription_id?: string }) {
    return this.http.post(`${this.base}/api/stripe/confirm-session`, payload);
  }

  openStripeCustomerPortal(body: { hubId: string }) {
    return this.http.post(`${this.base}/api/stripe/customer-portal`, body);
  }
}
